"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RedefiningModernHair() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="410" id="redefining-modern-hair" className="relative w-full min-h-screen bg-background text-foreground overflow-hidden font-default">
      {}
      <div className="absolute inset-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/premium_salon_editorial_hero_v2.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/premium_salon_editorial_hero_v2.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/premium_salon_editorial_hero_v2.png-wvc-sizes" alt="Elegant modern salon interior with premium styling stations and editorial lighting" className="w-full h-full object-cover scale-105 animate-[kenburns_20s_cubic-bezier(0.4,0,0.2,1)_infinite_alternate]" loading="eager" decoding="async" />
        <div className="absolute inset-0 bg-black/40" />
        {}
        <div className="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t from-primary/80 via-primary/20 to-transparent" />
      </div>

      {}
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 grid grid-cols-12 gap-4 px-6 md:px-10 lg:px-16 pt-32 md:pt-48 pb-20">
          {}
          <div className="col-span-12 lg:col-span-8 lg:col-start-2 flex flex-col justify-end">
            {}
            <div ref={subRef} className={`mb-10 transition-all duration-1000 ease-out ${subVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="flex items-center gap-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary-foreground/90">Velvet Shears — Est. 2015</span>
                <div className="h-px w-12 bg-primary-foreground/30" />
              </div>
            </div>

            {}
            <h1 ref={headlineRef} className="font-default font-extrabold text-primary-foreground/95 shadow-sm leading-[0.9] tracking-wider py-6" style={{
            fontSize: "clamp(3.5rem, 9vw, 8rem)"
          }}>
              <span className={`block transition-all duration-1000 delay-100 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                Redefining
              </span>
              <span className={`block transition-all duration-1000 delay-300 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                the <span className="italic font-light text-primary-foreground/95">Modern</span>
              </span>
              <span className={`block transition-all duration-1000 delay-500 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                Silhouette.
              </span>
            </h1>

            {}
            <div className={`mt-10 md:mt-12 max-w-xl transition-all duration-1000 delay-700 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-default italic text-lg md:text-xl text-primary-foreground/95 shadow-sm leading-relaxed mb-10">
                Precision cuts. Vibrant color artistry. Bespoke styling for the modern individual.
              </p>

              {}
              <div className="flex flex-col sm:flex-row gap-5">
                <Link to="/LookbookPage">
                  <Button size="lg" className="group bg-primary-foreground text-primary hover:bg-primary-foreground/95 h-14 px-8 font-mono text-xs uppercase tracking-[0.2em] rounded-md transition-colors duration-300">
                    View Lookbook
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1.5" />
                  </Button>
                </Link>
                <Link to="/BookPage">
                  <Button size="lg" variant="outline" className="h-14 px-8 font-mono text-xs uppercase tracking-[0.2em] bg-transparent border-2 border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground hover:text-primary rounded-md transition-all duration-300">
                    Download Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="relative z-10 grid grid-cols-12 gap-4 px-6 md:px-10 lg:px-16 pb-12">
          {}
          <div className="col-span-6 flex items-end">
            <div className="flex flex-col items-start gap-4">
              <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-primary-foreground/60">
                Scroll
              </span>
              <div className="relative h-12 w-px bg-primary-foreground/10 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-primary-foreground/40 animate-[scrollLine_2.5s_cubic-bezier(0.4,0,0.2,1)_infinite]" />
              </div>
            </div>
          </div>

          {}
          <div className="col-span-6 flex items-end justify-end">
            <div className="hidden lg:flex items-center gap-4 [writing-mode:vertical-rl] rotate-180">
              <div className="h-px w-10 bg-primary-foreground/20 rotate-180" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-primary-foreground/70">VELVET SHEARS / STUDIO / 2026</span>
            </div>
            <div className="lg:hidden flex items-center gap-3">
              <div className="h-px w-6 bg-primary-foreground/20" />
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-primary-foreground/70">
                Velvet Shears / Studio / 2024
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none z-[1] hidden md:block">
        <div className="absolute top-0 bottom-0 left-[8.333%] w-px bg-primary-foreground/[0.04]" />
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-primary-foreground/[0.04]" />
        <div className="absolute top-0 bottom-0 right-[8.333%] w-px bg-primary-foreground/[0.04]" />
      </div>

      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(0%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>;
}